import React from 'react';
import { styled } from 'linaria/react';
import { theme } from '../Theming/Theming';
import ProductLink from '@jetshop/ui/ProductLink';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import NotifyWhenBack from '../ProductPage/StockStatus/NotifyWhenBack';
import { SubName } from '../CategoryPage/ProductCard';
import { VariantSelector } from '../ProductPage/VariantSelector/VariantSelector';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useProductValidationMessage } from '../ProductPage/helpers';
import StockStatusIndicator from '../ProductPage/StockStatus/StockStatusIndicator';
import { ButtonUI } from '../ui/ButtonUI';
import AddToCart from '../Cart/AddToCart';
import t from '@jetshop/intl';
import ImageSlider from '../ProductPage/ImageSlider/ImageSlider';
import { PriceWrapper, ShortDescription } from '../ProductPage/ProductDetails';
import { Price } from '../Price';
import SiblingProducts from '../ProductPage/SiblingProducts/SiblingProducts';
import { useState } from 'react';
import { useQuery } from 'react-apollo';
import styleProductQuery from './styleProductQuery.gql';

const ProductWrapper = styled('div')`
  border-bottom: 1px solid ${theme.colors.borderLight};
  padding: 2rem 0;
  background-color: white;
  &:first-child {
    border-top: 1px solid ${theme.colors.borderLight};
  }
`;

const ProductInnerWrapper = styled('div')`
  color: inherit;
  display: flex;
  width: 100%;
  flex-direction: column;
  .product-details {
    padding: 1rem 0;
    .product-info {
      .price-stock {
        display: flex;
        justify-content: space-between;
      }
    }
    h2 {
      font-weight: ${theme.productGrid.product.name.fontWeight} !important;
    }
    .product-button {
      margin-top: 1rem;
    }
  }
  ${theme.above.md} {
    flex-direction: row;
    flex-wrap: wrap;
    .product-image {
      width: 30%;
    }
    .product-details {
      padding: 0 2rem;
      width: 70%;
      .product-info {
        width: 100%;
      }
    }
  }
`;

const Link = styled(ProductLink)`
  display: flex;
  width: 100%;
  text-decoration: none;
`;

const StockStatusWrapper = styled('div')`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  .stock-label {
    //display: none;
  }
  .free-freight-stock {
    display: inline-block;
    margin: 0 0 0 0.5rem;
  }
`;

const VariantSelectorWrapper = styled('div')`
  margin-top: 1rem;
  > div {
    margin-bottom: 0;
    button {
    }
  }
`;

const ButtonWrapper = styled('div')`
  button {
    padding: 0 2rem;
    width: 100%;
    height: 40px;
  }
`;

const StylesWrapper = styled('div')``;

const StyleProduct = ({ product: incomingProduct, categoryPath }) => {
  const [activeProduct, setProduct] = useState(incomingProduct);

  //IF NOT COMPLETE DATA QUERY FOR EXTRA
  const result = useQuery(styleProductQuery, {
    variables: { articleNumber: activeProduct.articleNumber },
    errorPolicy: 'all',
    skip: activeProduct.variants,
  });

  //MERGE NEW DATA
  const product = {
    ...activeProduct,
    ...result?.data?.product,
  };

  const hasImages = product.images && product.images.length > 0;

  //GET VARIANTS HANDLING
  const variantHandler = useProductVariants(
    product.articleNumber ? product : undefined,
    {}
  );

  const {
    selectedVariant,
    validation: variantValidation,
    missingOptions,
  } = variantHandler;

  const stockStatus = useStockStatus(selectedVariant || product);
  const price = useDynamicPrice(product, selectedVariant);

  const { hasVariants } = product;
  const { validationMessage, enableValidation } = useProductValidationMessage({
    missingOptions,
    variantValidation,
    hasVariants,
    stockStatus,
  });
  return (
    <ProductWrapper data-testid="product">
      <ProductInnerWrapper>
        <div className="product-image">
          {hasImages && (
            <>
              <ImageSlider
                product={product}
                customData={[]}
                selectedVariation={selectedVariant}
                showThumbs={false}
                sizes={[1, 1, 1 / 4, 1 / 6]}
              />
            </>
          )}
        </div>
        <div className="product-details">
          <div className="product-info">
            <Link product={product} categoryPath={categoryPath}>
              <h3>{product.name}</h3>
            </Link>
            <SubName>{product.subName}</SubName>
            <ShortDescription product={product} />
            <SiblingProducts product={product} setProduct={setProduct} />
            {product.hasVariants ? (
              <VariantSelectorWrapper className="variant-wrapper">
                <VariantSelector
                  type="button"
                  product={product}
                  variantHandler={variantHandler}
                  showValidation={!!validationMessage}
                />
              </VariantSelectorWrapper>
            ) : null}
            <div className="price-stock">
              <PriceWrapper>
                <Price {...price} />
              </PriceWrapper>
              {stockStatus && (
                <StockStatusWrapper>
                  <StockStatusIndicator
                    status={stockStatus.status}
                    text={stockStatus.text}
                  />
                </StockStatusWrapper>
              )}
            </div>
          </div>
          <div className="product-button">
            {stockStatus.status === 'notifyWhenBack' ? (
              <NotifyWhenBack
                text={stockStatus.text}
                articleNumber={selectedVariant.articleNumber}
              />
            ) : (
              <ButtonWrapper>
                {product.isPackage ||
                product.customerComments?.length > 0 ||
                product.specifications ? (
                  <ButtonUI>
                    <Link product={product} categoryPath={categoryPath}>
                      {t('Go to product')}
                    </Link>
                  </ButtonUI>
                ) : (
                  <AddToCart
                    product={product}
                    quantity={1}
                    selectedVariation={selectedVariant}
                  >
                    {(add) => (
                      <ButtonUI
                        css={{ marginTop: '1rem' }}
                        className="buy"
                        onClick={() => {
                          if (variantValidation === 'valid') {
                            add();
                          } else enableValidation();
                        }}
                      >
                        {t('Add to cart')}
                      </ButtonUI>
                    )}
                  </AddToCart>
                )}
              </ButtonWrapper>
            )}
          </div>
        </div>
      </ProductInnerWrapper>
    </ProductWrapper>
  );
};

const StylesProducts = ({ category, categoryPath }) => {
  const products = category?.products?.result;
  return (
    <StylesWrapper>
      {products.map((product, index) => (
        <StyleProduct
          key={index}
          product={product}
          categoryPath={categoryPath}
        />
      ))}
    </StylesWrapper>
  );
};

export default StylesProducts;
